import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
  useIsMobile,
} from '@design-system';

import { X } from 'lucide-react';

import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';

import { toast } from 'sonner';
import { fullName } from '@shared';
import { WaterlilyClient } from '@client/api/waterlily';
import { selectSessionAdvisor } from '@client/model/selectors';
import { fetchClientByClientIdRequest } from '@client/model/clientModel';
import { fetchClientsForAdvisorByAdvisorIdRequest } from '@client/model/clientsModel';

interface EditPartnerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: Client;
  partner: Client;
}

export function EditPartnerDialog({ open, onOpenChange, client, partner }: EditPartnerDialogProps) {
  const isMobile = useIsMobile();
  const [pendingUnlink, setPendingUnlink] = useState(false);
  const dispatch = useDispatch();
  const advisor = useSelector(selectSessionAdvisor);

  useEffect(() => {
    if (open) setPendingUnlink(false);
  }, [open]);

  const unlinkMutation = useMutation({
    mutationFn: () =>
      WaterlilyClient.put<void>(`/client/${client.clientId}/client-partner-link`, {
        partnerClientId: null,
      }),
    onSuccess: () => {
      dispatch(fetchClientByClientIdRequest(client.clientId));
      if (advisor?.advisorId) {
        dispatch(
          fetchClientsForAdvisorByAdvisorIdRequest({
            advisorId: advisor.advisorId,
            isInvalidation: true,
          }),
        );
      }
      toast.success('Partner unlinked successfully');
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to unlink partner');
    },
  });

  const title = `Edit Partner for ${fullName(client)}`;
  const description = 'Your currently linked partner is shown below.';

  const content = (
    <>
      <h2 className="text-sm text-gray-950">Current partner</h2>
      <Item
        variant="muted"
        className="bg-muted gap-3 p-2"
      >
        <ItemMedia
          variant="icon"
          className="size-10"
        >
          {partner.clientFirstName.charAt(0) + partner.clientLastName.charAt(0)}
        </ItemMedia>
        <ItemContent className="gap-0">
          <ItemTitle>{fullName(partner)}</ItemTitle>
          {partner.clientEmail && <ItemDescription>{partner.clientEmail}</ItemDescription>}
        </ItemContent>
        {!pendingUnlink && (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setPendingUnlink(true)}
            className="hover:bg-destructive/5 text-foreground hover:text-destructive mr-0.5 rounded-sm"
          >
            <X />
          </Button>
        )}
      </Item>
    </>
  );

  const footer = (
    <DialogFooter>
      <Button
        variant="outline"
        onClick={() => onOpenChange(false)}
      >
        {pendingUnlink ? 'Cancel' : 'Close'}
      </Button>
      {pendingUnlink && (
        <Button
          variant="destructive"
          onClick={() => unlinkMutation.mutate()}
          disabled={unlinkMutation.isPending}
        >
          {unlinkMutation.isPending ? 'Unlinking...' : 'Confirm Unlink'}
        </Button>
      )}
    </DialogFooter>
  );

  if (!isMobile) {
    return (
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
      >
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          {content}
          {footer}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
    >
      <DrawerContent className="max-h-[90vh] overflow-y-auto px-4 pb-6">
        <DrawerHeader className="text-left">
          <DrawerTitle>{title}</DrawerTitle>
          <DrawerDescription>{description}</DrawerDescription>
        </DrawerHeader>
        {content}
        <DrawerFooter className="px-0">{footer}</DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
